import { lazy, Suspense, useEffect, createContext, useReducer, useState, Fragment } from 'react';
import { BrowserRouter, Switch, Route, useLocation } from 'react-router-dom';
import { Transition, TransitionGroup } from 'react-transition-group';
import classNames from 'classnames';
import { Helmet } from 'react-helmet';
import Navbar from 'components/Navbar';
import ThemeProvider from 'components/ThemeProvider';
import { tokens } from 'components/ThemeProvider/theme';
import VisuallyHidden from 'components/VisuallyHidden';
import { useLocalStorage } from 'hooks';
import { msToNum } from 'utils/style';
import { reflow } from 'utils/transition';
import prerender from 'utils/prerender';
import { initialState, reducer } from 'app/reducer';
import Loading from 'pages/loader';
import './index.css';
import './reset.css';
import { Analytics } from '@vercel/analytics/react';

import Videography from 'pages/videography';

const Home = lazy(() => import('pages/Home'));
const Contact = lazy(() => import('pages/Contact'));
const Page404 = lazy(() => import('pages/404'));
const client = lazy(() => import('pages/OurClients'));

export const AppContext = createContext();
export const TransitionContext = createContext();

const App = () => {
  const [storedTheme] = useLocalStorage('theme', 'dark');
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isLoading, setIsLoading] = useState(true); 

  useEffect(() => {
    if (!prerender) {
      console.info('\n\n');
    }
    window.history.scrollRestoration = 'manual';
    const timer = setTimeout(() => setIsLoading(false), 7050);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    dispatch({ type: 'setTheme', value: storedTheme });
  }, [storedTheme]);

  return (
    <AppContext.Provider value={{ ...state, dispatch }}>
      <ThemeProvider themeId={state.theme}>
        <BrowserRouter>
          {isLoading && <Loading />}
          <div style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
            <AppRoutes />
          </div>
          <Analytics />
        </BrowserRouter>
      </ThemeProvider>
    </AppContext.Provider>
  );
};

const AppRoutes = () => {
  const location = useLocation();
  const { pathname } = location;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <Fragment>
<Helmet>
        <title>Shutterfly EG | Photography & Videography</title>
        <meta 
          name="description" 
          content="Discover top-tier photography and videography services by Shutterfly Egypt. Capture your best moments with creativity and high-quality visuals." 
        />
        <meta name="keywords" content="Shutterfly Egypt, photography, videography, creative visuals, professional services, Egypt media" />
        <meta property="og:title" content="Shutterfly EG | Photography & Videography" />
        <meta property="og:description" content="High-quality photography and videography services in Egypt. Let Shutterfly capture your special moments creatively." />
        <meta property="og:image" content="https://shutterfly-eg.com/static/media/shutter.2792be9662ed8602e4c5.jpg" />
        <meta property="og:url" content="https://shutterfly-eg.com/" />
        <meta property="og:site_name" content="Shutterfly EG" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://shutterfly-eg.com/" />
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Shutterfly EG",
            "url": "https://shutterfly-eg.com/",
            "logo": "https://shutterfly-eg.com/static/media/shutter.2792be9662ed8602e4c5.jpg",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+201210864493",
              "contactType": "Customer Service"
            }
          }
          `}
        </script>
      </Helmet>
      <VisuallyHidden showOnFocus as="a" className="skip-to-main" href="#MainContent">
        Skip to main content
      </VisuallyHidden>
      <Navbar location={location} />
      <TransitionGroup component="main" className="app" tabIndex={-1} id="MainContent">
        <Transition
          key={pathname}
          timeout={msToNum(tokens.base.durationS)}
          onEnter={reflow}
        >
          {status => (
            <TransitionContext.Provider value={{ status }}>
              <div className={classNames('app__page', `app__page--${status}`)}>
                <Suspense fallback={<Loading />}>
                  <Switch location={location}>
                    <Route exact path="/" component={Home} />
                    <Route path="/contact" component={Contact} />
                    <Route path="/OurClients" component={client} />
                    <Route path="/videography/:param" component={Videography} />
                    <Route component={Page404} />
                  </Switch>
                </Suspense>
              </div>
            </TransitionContext.Provider>
          )}
        </Transition>
      </TransitionGroup>
    </Fragment>
  );
};

export default App;
